import { useAuthStore } from '@/stores/auth'
import { env } from '@/utils/env.js'
import { routeBlockedRoles, routePermissionRoleFallbacks, routePermissions } from '@/router/routePermissions.js'

/**
 * Authentication middleware for route guards
 */
export const authMiddleware = {
  /**
   * Route guard that requires authentication
   */
  requireAuth: async (to, from, next) => {
    const authStore = useAuthStore()
    
    // Ensure auth store is initialized before checking authentication
    if (!authStore.user && !authStore.token) {
      env.debug('Auth store not initialized, initializing now...')
      await authStore.initializeAuth()
    }
    
    env.debug('Auth guard check:', {
      to: to.name,
      isAuthenticated: authStore.isAuthenticated,
      hasToken: !!authStore.token,
      hasUser: !!authStore.user
    })
    
    if (authStore.isAuthenticated) {
      next()
    } else {
      env.warn('Access denied - authentication required:', to.path)
      next({
        name: 'login',
        query: { redirect: to.fullPath }
      })
    }
  },

  /**
   * Route guard that requires guest (not authenticated)
   */
  requireGuest: (to, from, next) => {
    const authStore = useAuthStore()
    
    // Ensure auth store is initialized before checking authentication
    if (!authStore.user && !authStore.token) {
      env.debug('Auth store not initialized for guest check, initializing now...')
      authStore.initializeAuth()
    }
    
    env.debug('Guest guard check:', {
      to: to.name,
      isAuthenticated: authStore.isAuthenticated,
      hasToken: !!authStore.token,
      hasUser: !!authStore.user
    })
    
    if (!authStore.isAuthenticated) {
      env.debug('Not authenticated, allowing access to auth page')
      next()
    } else {
      env.debug('Already authenticated, redirecting to home')
      next({ name: 'home-default' })
    }
  },

  /**
   * Route guard that requires specific roles
   */
  requireRole: (roles) => {
    return (to, from, next) => {
      const authStore = useAuthStore()
      
      if (!authStore.isAuthenticated) {
        next({
          name: 'login',
          query: { redirect: to.fullPath }
        })
        return
      }
      
      const hasRequiredRole = Array.isArray(roles) 
        ? authStore.hasAnyRole(roles)
        : authStore.hasRole(roles)
      
      if (hasRequiredRole) {
        next()
      } else {
        env.warn('Access denied - insufficient role permissions:', {
          required: roles,
          userRoles: authStore.userRoles
        })
        next(deniedTo(to, { roles: [].concat(roles) }))
      }
    }
  },

  /**
   * Route guard that requires specific permissions
   */
  requirePermission: (permissions) => {
    return (to, from, next) => {
      const authStore = useAuthStore()
      
      if (!authStore.isAuthenticated) {
        next({
          name: 'login',
          query: { redirect: to.fullPath }
        })
        return
      }
      
      const hasRequiredPermission = Array.isArray(permissions)
        ? authStore.hasAnyPermission(permissions)
        : authStore.hasPermission(permissions)
      
      if (hasRequiredPermission) {
        next()
      } else {
        env.warn('Access denied - insufficient permissions:', {
          required: permissions,
          userPermissions: authStore.userPermissions
        })
        next(deniedTo(to, { permissions: [].concat(permissions) }))
      }
    }
  }
}

/**
 * Where a denied navigation goes: the access-denied screen, told what the screen
 * required so it can state the reason instead of refusing without explanation.
 */
function deniedTo(to, { permissions = [], roles = [] } = {}) {
  const query = { screen: to.name ?? to.path }

  if (permissions.length) query.required = permissions
  if (roles.length) query.requiredRoles = roles

  return { name: 'access-denied', query }
}

/**
 * Initialize authentication guards
 */
export function initializeAuthGuards(router) {
  env.debug('Initializing authentication guards')
  
  router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore()
    
    // Routes that don't require authentication
    const publicRoutes = [
      'login', 
      'forgot-password', 
      'screen-locked',
      'sso-callback',
      'new-password',
      'verification',
      'oops',
      'notfound'
    ]

    // Routes that every authenticated user can always access (no permission check)
    const alwaysAllowedRoutes = [
      'home-default',
      'home',
    ]
    
    // Auth routes that should redirect if already authenticated
    const authRoutes = ['login']
    
    env.debug('Route navigation:', {
      from: from.name,
      to: to.name,
      isPublic: publicRoutes.includes(to.name),
      isAuthRoute: authRoutes.includes(to.name),
      isAuthenticated: authStore.isAuthenticated
    })
    
    // Initialize auth store if not already done (always fetch fresh /me for up-to-date roles)
    if (!authStore.authInitialized) {
      await authStore.initializeAuth()
    }
    
    // Redirect authenticated users away from the login page.
    if (authRoutes.includes(to.name) && authStore.isAuthenticated) {
      env.debug('Already authenticated, redirecting from auth page to home')
      return next({ name: 'home-default' })
    }
    
    // Handle public routes
    if (publicRoutes.includes(to.name)) {
      return next()
    }
    
    // Handle protected routes
    if (!authStore.isAuthenticated) {
      env.warn('Not authenticated, redirecting to login')
      return next({
        name: 'login',
        query: { redirect: to.fullPath }
      })
    }

    // Home and other always-accessible routes — skip all permission checks
    if (alwaysAllowedRoutes.includes(to.name)) {
      return next()
    }
    
    // Explicit role gates are identity checks and are never permission-bypassed.
    if (to.meta?.requiresRole) {
      const hasRequiredRole = Array.isArray(to.meta.requiresRole)
        ? authStore.hasAnyRole(to.meta.requiresRole)
        : authStore.hasRole(to.meta.requiresRole)
      
      if (!hasRequiredRole) {
        env.warn('Access denied - insufficient role:', {
          required: to.meta.requiresRole,
          userRoles: authStore.userRoles
        })
        return next(deniedTo(to, { roles: [].concat(to.meta.requiresRole) }))
      }
    }

    // Both roles have full feature permissions after exact role gates pass.
    const isAdministrativeUser = authStore.hasAnyRole(['Super Admin', 'Admin'])
    if (isAdministrativeUser) {
      return next()
    }
    
    if (to.meta?.requiresPermission) {
      const hasRequiredPermission = Array.isArray(to.meta.requiresPermission)
        ? authStore.hasAnyPermission(to.meta.requiresPermission)
        : authStore.hasPermission(to.meta.requiresPermission)
      const fallbackRoles = routePermissionRoleFallbacks[to.name]
      const hasFallbackRole = Array.isArray(fallbackRoles) && authStore.hasAnyRole(fallbackRoles)
      
      if (!hasRequiredPermission && !hasFallbackRole) {
        env.warn('Access denied - insufficient permission:', {
          required: to.meta.requiresPermission,
          fallbackRoles,
          userPermissions: authStore.userPermissions
        })
        return next(deniedTo(to, {
          permissions: [].concat(to.meta.requiresPermission),
          roles: fallbackRoles ?? [],
        }))
      }
    }
    
    // Roles barred from a route regardless of their permissions. Checked before the
    // permission map so holding the permission cannot talk its way past the block.
    const blockedRoles = routeBlockedRoles[to.name]
    if (Array.isArray(blockedRoles) && authStore.hasAnyRole(blockedRoles)) {
      env.warn('Access denied - role is blocked from route:', { route: to.name, blockedRoles })
      return next(deniedTo(to, { permissions: [], roles: blockedRoles }))
    }

    // Centralised permission map (route name → required permission)
    const mappedPermission = routePermissions[to.name]
    if (mappedPermission) {
      const hasMapped = Array.isArray(mappedPermission)
        ? authStore.hasAnyPermission(mappedPermission)
        : authStore.hasPermission(mappedPermission)
      const fallbackRoles = routePermissionRoleFallbacks[to.name]
      const hasFallbackRole = Array.isArray(fallbackRoles) && authStore.hasAnyRole(fallbackRoles)

      if (!hasMapped && !hasFallbackRole) {
        env.warn('Access denied - missing permission for route:', {
          route: to.name,
          required: mappedPermission,
          fallbackRoles,
          userPermissions: authStore.userPermissions?.map(p => p.name ?? p)
        })
        return next(deniedTo(to, {
          permissions: [].concat(mappedPermission),
          roles: fallbackRoles ?? [],
        }))
      }
    }
    
    // All checks passed
    next()
  })
  
  env.debug('Authentication guards initialized successfully')
}
